import type { ImmutableArray, Nullish, ValidatorType } from "shelving";
import { ALLOW_STRING, BOOLEAN, DATA, INTEGER, KEY, notNullish, pickProps } from "shelving";
import { IMAGE } from "shared/util/media";
import { KEYWORD_FIELDS } from "../util/keyword.js";
import { PRIVACY } from "../util/privacy.js";
import { CREATOR_ENTITY, ENTITIES } from "./entity.js";
import { ABOUT, HUE, LongformSchema, NameSchema } from "./misc.js";

export const REWARD_MAX = 48;
export const EARNED_MAX = 48;

export type RewardType = "badge" | "background" | "reaction";
export const REWARD_TYPES: {
	[K in RewardType]: string;
} = {
	badge: "Badge",
	background: "Background",
	reaction: "Reaction",
	// bling: "Bling",
	// frame: "Frame",
};

/** Rewards like badges, backgrounds, reactions. */
export const REWARD_SCHEMA = DATA({
	creator: CREATOR_ENTITY,
	verified: BOOLEAN,
	slug: KEY, // Path segment shown in the URL based on the title, e.g. `gold-badge` (created with `toSlug()`).
	type: ALLOW_STRING(REWARD_TYPES),
	name: new NameSchema({ title: "Name", placeholder: "e.g. Vermillion City Badge" }),
	about: ABOUT,
	tags: ENTITIES,
	...KEYWORD_FIELDS,
	secret: new LongformSchema({ title: "Secret message", max: 100 }),
	image: IMAGE,
	hue: HUE,
	// Special.
	creatable: BOOLEAN, // e.g. badges and backgrounds that can be picked in the Challenge creator.
	unlocked: BOOLEAN, // e.g. backgrounds and reactions that are always unlocked by default.
	// Counts.
	users: INTEGER, // Number of users who have earned the reward (globally).
	earned: INTEGER, // Number of times the reward has been earned (globally).
	reactions: INTEGER,
	// Dates.
	created: INTEGER,
	updated: INTEGER,
});
export type RewardData = ValidatorType<typeof REWARD_SCHEMA>;

/** Form that can create a reward uses a subset of the values. */
export const REWARD_FORM_SCHEMA = DATA(pickProps(REWARD_SCHEMA.props, "creator", "name", "about", "secret", "image", "hue"));
export type RewardFormData = ValidatorType<typeof REWARD_FORM_SCHEMA>;

/** Earned rewards for a user. */
export const EARNED_SCHEMA = DATA({
	uid: KEY,
	rid: KEY,
	type: REWARD_SCHEMA.props.type,
	count: INTEGER,
	created: INTEGER,
	updated: INTEGER,
	privacy: PRIVACY,
});
export type EarnedData = ValidatorType<typeof EARNED_SCHEMA>;

/** Get the ID for an earned document. */
export const getEarnedID = (rid: string, uid: string) => `${uid}:${rid}`;

/** Turn an array of reward IDs into an array of reward entities like `reward:abc123` */
export function getRewardEntities(...ids: Nullish<string>[]): ImmutableArray<`reward:${string}`> {
	return ids.filter(notNullish).map((id: string): `reward:${string}` => `reward:${id}`);
}
